// IncomingCallScreen — modal shown when another user is calling the
// current user. Visual style matches the rest of the app:
//
//   * Backdrop:  bg-[#0A0F2D4D] backdrop-blur-md  (same as ContactSupportModal,
//                ConfirmationModal, etc.)
//   * Card:      white, rounded-2xl, slide-up animation on mobile
//   * Brand:     TP blue (#2283FF) header strip with the TP icon, mirroring
//                the home StandingsWidget topbar treatment.
//   * Avatar:    shared common/Avatar component (same image / initials
//                fallback used in chat list, headers, profile cards).
//   * Pulse:     a subtle ring around the avatar to signal "live incoming"
//                without leaning on a dark gradient.
//
// Rendered OUTSIDE the <StreamCall> provider — we don't have a call object
// yet, only an invitation payload — so it can't use the SDK hooks.

import React from "react";
import { Phone, PhoneOff } from "lucide-react";
import Avatar from "../../common/Avatar";
import tpIcon from "../../../assets/images/tp-icon.png";

export default function IncomingCallScreen({ incomingCall, onAccept, onDecline }) {
  if (!incomingCall) return null;

  const isVideo = incomingCall.type === "video";
  const callerName = incomingCall.caller_name || "Unknown caller";
  const callerImage = incomingCall.caller_image || null;
  const subtitle = isVideo ? "Incoming video call" : "Incoming voice call";

  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-[#0A0F2D4D] backdrop-blur-md p-0 sm:p-4">
      <style>{ringPulseKeyframes}</style>
      <div className="w-full sm:w-[420px] max-h-[92vh] bg-white rounded-t-2xl sm:rounded-2xl overflow-hidden shadow-xl motion-safe:animate-[slideUp_0.3s_ease-out_forwards]">

        {/* Branded header strip — matches the TP blue used on home widgets,
            buttons, and the floating support icon. */}
        <div className="bg-[#2283FF] text-white px-5 py-3 flex items-center gap-3">
          <img
            src={tpIcon}
            alt=""
            className="w-7 h-7 rounded-md flex-shrink-0"
          />
          <div className="flex-1 min-w-0">
            <div className="text-sm font-semibold leading-tight truncate">
              {subtitle}
            </div>
            <div className="text-[11px] opacity-80 leading-tight">
              TransferPitch · tap to answer
            </div>
          </div>
        </div>

        {/* Caller block */}
        <div className="px-6 pt-8 pb-2 flex flex-col items-center">
          <div className="relative">
            {/* Animated rings — soft TP-blue pulse so the modal still
                feels "live" without resorting to a dark gradient. */}
            <span className="incoming-call-ring incoming-call-ring--1" />
            <span className="incoming-call-ring incoming-call-ring--2" />
            <Avatar
              src={callerImage}
              alt={callerName}
              fallbackText={callerName}
              size={104}
              className="relative ring-4 ring-white shadow-md"
            />
          </div>

          <h3 className="text-[#1A212F] text-xl font-bold text-center mt-5 truncate max-w-full">
            {callerName}
          </h3>
          <p className="text-gray-500 text-sm text-center mt-1">{subtitle}</p>
        </div>

        {/* Actions */}
        <div className="px-6 pb-8 pt-6 flex justify-center gap-10">
          <ActionButton
            label="Decline"
            onClick={onDecline}
            colorClass="bg-red-500 hover:bg-red-600"
            icon={<PhoneOff size={22} />}
          />
          <ActionButton
            label="Accept"
            onClick={onAccept}
            colorClass="bg-green-500 hover:bg-green-600"
            icon={<Phone size={22} />}
          />
        </div>
      </div>
    </div>
  );
}

function ActionButton({ label, onClick, colorClass, icon }) {
  return (
    <div className="flex flex-col items-center gap-2">
      <button
        type="button"
        onClick={onClick}
        aria-label={label}
        className={`w-14 h-14 rounded-full text-white flex items-center justify-center transition-colors shadow-sm ${colorClass}`}
      >
        {icon}
      </button>
      <span className="text-sm font-medium text-[#1A212F]">{label}</span>
    </div>
  );
}

// Scoped keyframes for the two rings — keeps the animation self-contained
// so we don't add another global CSS rule.
const ringPulseKeyframes = `
.incoming-call-ring {
  position: absolute;
  inset: 0;
  border-radius: 9999px;
  border: 2px solid #2283FF;
  opacity: 0.6;
  pointer-events: none;
  animation: incomingCallPulse 1.8s ease-out infinite;
}
.incoming-call-ring--2 { animation-delay: 0.9s; }
@keyframes incomingCallPulse {
  0%   { transform: scale(1);    opacity: 0.55; }
  80%  { transform: scale(1.35); opacity: 0;    }
  100% { transform: scale(1.35); opacity: 0;    }
}
`;
